import React, { useRef, useEffect, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { dataGridAmbientBaseSx, TABLE_BORDER_AMBIENT } from "../../utils/colors";

const ROW_HEIGHT = 64;
const HEADER_HEIGHT = 72;

const dualPaneGridSx = [
  dataGridAmbientBaseSx,
  {
    "& .MuiDataGrid-columnHeaders": {
      fontWeight: 700,
      fontSize: "0.8rem",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
    },
    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
  },
];

/**
 * DualPaneDataGrid
 *
 * Simulates a frozen first column by rendering two synchronized DataGrids side
 * by side.  The left grid shows only `leftColumns` (fixed width, no scroll),
 * the right grid shows `rightColumns` and is horizontally scrollable.
 *
 * Vertical scroll is kept in sync between the two virtual scrollers so both
 * panes always show the same rows.
 */
export default function DualPaneDataGrid({
  rows,
  leftColumns,
  rightColumns,
  leftWidth = 280,
  paginationModel,
  onPaginationModelChange,
  pageSizeOptions = [10, 25, 50],
  rowSelectionModel,
  onRowSelectionModelChange,
  onRowClick,
  onCellClick,
  disableColumnSorting = true,
}) {
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const isSyncing = useRef(false);

  // ── Vertical scroll synchronisation ─────────────────────────────────────
  const attachSync = useCallback(() => {
    const leftEl = leftRef.current?.querySelector(
      ".MuiDataGrid-virtualScroller",
    );
    const rightEl = rightRef.current?.querySelector(
      ".MuiDataGrid-virtualScroller",
    );
    if (!leftEl || !rightEl) return;

    const syncFromRight = () => {
      if (isSyncing.current) return;
      isSyncing.current = true;
      leftEl.scrollTop = rightEl.scrollTop;
      isSyncing.current = false;
    };

    const syncFromLeft = () => {
      if (isSyncing.current) return;
      isSyncing.current = true;
      rightEl.scrollTop = leftEl.scrollTop;
      isSyncing.current = false;
    };

    rightEl.addEventListener("scroll", syncFromRight, { passive: true });
    leftEl.addEventListener("scroll", syncFromLeft, { passive: true });

    return () => {
      rightEl.removeEventListener("scroll", syncFromRight);
      leftEl.removeEventListener("scroll", syncFromLeft);
    };
  }, []);

  useEffect(() => {
    // Give DataGrid time to mount the virtual scroller DOM
    const t = setTimeout(() => {
      const cleanup = attachSync();
      // store cleanup for inner effect cleanup
      return cleanup;
    }, 150);
    return () => clearTimeout(t);
  }, [attachSync, rows, paginationModel]);

  // ── Props shared by both grids (layout/display only) ────────────────────
  const sharedBase = {
    rows,
    rowHeight: ROW_HEIGHT,
    columnHeaderHeight: HEADER_HEIGHT,
    autoHeight: true,
    disableColumnMenu: true,
    disableColumnFilter: true,
    disableColumnSorting,
  };

  return (
    <Box sx={{ display: "flex", width: "100%", overflow: "hidden" }}>
      {/* ── Frozen left column ──────────────────────────────────────── */}
      <Box
        ref={leftRef}
        sx={{
          width: leftWidth,
          flexShrink: 0,
          borderRight: (theme) =>
            theme.palette.mode === "dark"
              ? `1px solid ${TABLE_BORDER_AMBIENT}`
              : "2px solid",
          borderColor: "divider",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "4px 0 16px rgba(0,0,0,0.25)"
              : "4px 0 10px rgba(0,0,0,0.1)",
          zIndex: 1,
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "hidden !important",
          },
          "& .MuiDataGrid-scrollbarFiller": { display: "none" },
        }}
      >
        <DataGrid
          {...sharedBase}
          columns={leftColumns}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={pageSizeOptions}
          hideFooter
          disableRowSelectionOnClick
          sx={dualPaneGridSx}
        />
      </Box>

      {/* ── Scrollable right pane ───────────────────────────────────── */}
      <Box ref={rightRef} sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <DataGrid
          {...sharedBase}
          columns={rightColumns}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={pageSizeOptions}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          onRowClick={onRowClick}
          onCellClick={onCellClick}
          disableRowSelectionOnClick={false}
          sx={[
            ...dualPaneGridSx,
            {
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark" ? TABLE_BORDER_AMBIENT : "divider",
              },
            },
          ]}
        />
      </Box>
    </Box>
  );
}
